import { useEffect } from "react";
import { FiX, FiDownload, FiSearch } from "react-icons/fi";
import { LuStore, LuUsers } from "react-icons/lu";
import * as XLSX from "xlsx-js-style";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const formatMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return `€${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const isBusinessRecord = (rows = []) => !!rows?.[0]?.business_nui;

/* English property key -> Albanian column header */
const COLUMN_LABELS = {
  user_id: "ID",
  id: "ID",
  first_name: "Emri",
  last_name: "Mbiemri",
  email: "Email",
  phone_number: "Telefoni",
  personal_number: "Numri personal",
  tier_level: "Kushti",
  prize_amount: "Shuma fituese",
  receipt_count: "Kuponat",
  amount_count: "Totali",
  cashback_amount: "Cashback",
  business_nui: "NUI",
  business_name: "Biznesi",
  fiscal_number: "Numri fiskal",
  municipality_no: "Komuna",
  total_amount: "Totali",
};

const labelFor = (key) => COLUMN_LABELS[key] || key;

/* Drop keys that resolve to a label already taken (e.g. user_id + id -> "ID") */
const dedupeByLabel = (keys) => {
  const seen = new Set();
  return keys.filter((k) => {
    const label = labelFor(k);
    if (seen.has(label)) return false;
    seen.add(label);
    return true;
  });
};

/* ------------------------------ Exports ------------------------------- */

const exportToExcel = (rows) => {
  if (!rows?.length) {
    alert("Nuk ka të dhëna për të gjeneruar");
    return;
  }
  const allProps = new Set();
  rows.forEach((r) =>
    Object.keys(r).forEach((k) => {
      if (r[k] !== null && r[k] !== undefined && r[k] !== "") allProps.add(k);
    })
  );
  const props = dedupeByLabel(Array.from(allProps));
  const formatted = rows.map((r, i) => {
    const out = { "#": i + 1 };
    props.forEach((p) => {
      if (r[p] !== null && r[p] !== undefined && r[p] !== "") out[labelFor(p)] = r[p];
    });
    return out;
  });
  const ws = XLSX.utils.json_to_sheet(formatted);
  const cols = Object.keys(formatted[0] || {});
  ws["!cols"] = cols.map((k) => ({
    wch:
      Math.max(
        k.length,
        ...formatted.map((row) => (row[k] ? row[k].toString().length : 0))
      ) + 2,
  }));

  /* Styling to match the PDF look */
  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
    fill: { fgColor: { rgb: "374151" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "E5E7EB" } },
      bottom: { style: "thin", color: { rgb: "E5E7EB" } },
      left: { style: "thin", color: { rgb: "E5E7EB" } },
      right: { style: "thin", color: { rgb: "E5E7EB" } },
    },
  };
  const cellBorder = {
    top: { style: "thin", color: { rgb: "F3F4F6" } },
    bottom: { style: "thin", color: { rgb: "F3F4F6" } },
    left: { style: "thin", color: { rgb: "F3F4F6" } },
    right: { style: "thin", color: { rgb: "F3F4F6" } },
  };
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (!cell) continue;
      if (R === 0) {
        cell.s = headerStyle;
      } else {
        cell.s = {
          alignment: { vertical: "center" },
          fill: { fgColor: { rgb: R % 2 === 0 ? "F9FAFB" : "FFFFFF" } },
          border: cellBorder,
        };
      }
    }
  }
  ws["!rows"] = [{ hpt: 22 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Fituesit");
  XLSX.writeFile(wb, "Fituesit_GIZ_Dashboard.xlsx");
};

const exportToPdf = (rows, isBusiness) => {
  if (!rows?.length) {
    alert("Nuk ka të dhëna për të gjeneruar");
    return;
  }
  const allProps = new Set();
  rows.forEach((r) =>
    Object.keys(r).forEach((k) => {
      if (r[k] !== null && r[k] !== undefined && r[k] !== "") allProps.add(k);
    })
  );
  const props = dedupeByLabel(Array.from(allProps));
  const head = [["#", ...props.map(labelFor)]];
  const body = rows.map((r, i) => [
    i + 1,
    ...props.map((p) =>
      r[p] !== null && r[p] !== undefined && r[p] !== "" ? String(r[p]) : ""
    ),
  ]);

  const pdf = new jsPDF({ orientation: "landscape" });
  pdf.setFontSize(16);
  pdf.text(isBusiness ? "Lista e Bizneseve Fituese" : "Lista e Fituesve", 14, 12);
  autoTable(pdf, {
    head,
    body,
    startY: 18,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [55, 65, 81], textColor: 255 },
  });
  pdf.save(isBusiness ? "Bizneset_GIZ_Dashboard.pdf" : "Fituesit_GIZ_Dashboard.pdf");
};

/* ------------------------------- Tables ------------------------------- */

function UserTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-gray-50 border-b border-gray-200">
          <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">ID</th>
          <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Emri dhe mbiemri</th>
          {rows[0]?.receipt_count !== undefined && (
            <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Kuponat</th>
          )}
          {rows[0]?.amount_count !== undefined && (
            <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Totali</th>
          )}
          {rows[0]?.cashback_amount !== undefined && (
            <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Cashback</th>
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((u, idx) => (
          <tr key={u.user_id ?? idx} className="hover:bg-gray-50/70 transition-colors">
            <td className="px-4 py-2.5 text-gray-500 tabular-nums text-xs">#{u.user_id}</td>
            <td className="px-4 py-2.5 font-medium text-gray-900 truncate">
              {(u.first_name || u.last_name)
                ? `${u.first_name || ""} ${u.last_name || ""}`.trim()
                : "—"}
            </td>
            {rows[0]?.receipt_count !== undefined && (
              <td className="px-4 py-2.5 text-right tabular-nums">{u.receipt_count ?? "—"}</td>
            )}
            {rows[0]?.amount_count !== undefined && (
              <td className="px-4 py-2.5 text-right tabular-nums">{formatMoney(u.amount_count)}</td>
            )}
            {rows[0]?.cashback_amount !== undefined && (
              <td className="px-4 py-2.5 text-right tabular-nums">{formatMoney(u.cashback_amount)}</td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BusinessTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-gray-50 border-b border-gray-200">
          <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">NUI</th>
          <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Biznesi</th>
          {rows[0]?.receipt_count !== undefined && (
            <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Kuponat</th>
          )}
          {rows[0]?.total_amount !== undefined && (
            <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Totali</th>
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((b, idx) => (
          <tr key={b.business_nui ?? idx} className="hover:bg-gray-50/70 transition-colors">
            <td className="px-4 py-2.5 text-gray-500 tabular-nums text-xs">{b.business_nui || "—"}</td>
            <td className="px-4 py-2.5">
              <div className="font-medium text-gray-900 truncate">{b.business_name || "—"}</div>
              {b.fiscal_number && (
                <div className="text-xs text-gray-500 tabular-nums">Fiscal {b.fiscal_number}</div>
              )}
            </td>
            {rows[0]?.receipt_count !== undefined && (
              <td className="px-4 py-2.5 text-right tabular-nums">{b.receipt_count ?? "—"}</td>
            )}
            {rows[0]?.total_amount !== undefined && (
              <td className="px-4 py-2.5 text-right tabular-nums">{formatMoney(b.total_amount)}</td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* -------------------------------- Modal ------------------------------- */

const UsersModal = ({ isOpen, handleClose, users = [] }) => {
  /* Esc to close */
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const isBusiness = isBusinessRecord(users);
  const Icon = isBusiness ? LuStore : LuUsers;
  const title = isBusiness ? "Bizneset që plotësojnë kushtin" : "Lojtarët që kanë fituar kushtin";
  const subtitle = `${users.length.toLocaleString()} ${isBusiness ? "biznese" : "lojtarë"} potencialë`;

  return (
    <div
      onClick={handleClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-3xl max-h-[85vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[var(--secondary-color)]/10 text-[var(--secondary-color)]">
              <Icon size={18} />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 truncate">{title}</h2>
              <p className="text-xs text-gray-500 tabular-nums">{subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100 transition"
            aria-label="Close"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {users.length > 0 ? (
            <div className="overflow-x-auto">
              {isBusiness ? <BusinessTable rows={users} /> : <UserTable rows={users} />}
            </div>
          ) : (
            <div className="py-16 px-6 flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center mb-4">
                <FiSearch size={22} className="text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-800">
                Asnjë rezultat për këtë nivel
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {isBusiness
                  ? "Nuk ka biznese që plotësojnë kushtin për këtë lojë."
                  : "Nuk ka fitues potencialë për këtë nivel."}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {users.length > 0 && (
          <div className="px-5 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => exportToExcel(users)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition flex-1"
            >
              <FiDownload size={14} />
              Shkarko Excel
            </button>
            <button
              type="button"
              onClick={() => exportToPdf(users, isBusiness)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 active:scale-[0.98] px-4 py-2 text-sm font-semibold text-white shadow-sm transition flex-1"
            >
              <FiDownload size={14} />
              Shkarko PDF
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersModal;
